import { DefaultValues } from 'react-hook-form'

import { getDefaultPageState } from '../../direct-flow/state/config'
import { convertIsoDateIntoDateObj } from '../date/date'
import { resolveYearsInUk } from '../transformStateToDetailsList/helpers/helpers'
import { POLICY_HOLDER_ID } from 'directFlow/constants'
import { PersistentState } from 'directFlow/state/persistentState/type'
import {
  AdditionalDriverItem,
  ClaimItem,
  ConvictionItem,
  SettlementType,
} from 'directFlow/state/types'
import { Page } from 'directFlow/types'
import { generateMonthString, isUKLicenceType } from 'directFlow/utils'

import {
  EmploymentStatus,
  LicenceType,
  MedicalCondition,
  OtherVehicleUse,
  PreviousInsuranceCancellationReasons,
  QuoteV2,
  TestGroup,
  VehicleOwnerType,
  VehicleSteering,
  VehicleType,
} from 'types'
import { PossibleFeatureFlags } from 'utils'
import { PossibleAbTests } from 'utils/abTests/abTests.types'

type LicenceState = NonNullable<PersistentState['pageState']['licence']>

const getLicenceState = (data: QuoteV2): LicenceState => {
  const { licence } = data.policyHolder
  const defaultValues = getDefaultPageState(Page.LICENCE)

  const isProvisionalLicence = licence.provisional && licence.type === LicenceType.UK
  const pageState: LicenceState = {
    countryOfFirstTest: licence.countryOfFirstTest ?? defaultValues.countryOfFirstTest,
    licenceType: isProvisionalLicence ? LicenceType.UK_PROVISIONAL : (licence.type ?? undefined),
    ukDurationMonth:
      licence.ukDuration && 'month' in licence.ukDuration
        ? generateMonthString(licence.ukDuration.month!)
        : undefined,
    ukDurationYear:
      licence.ukDuration && 'year' in licence.ukDuration
        ? licence.ukDuration.year!.toString()
        : undefined,
    durationMonth:
      'month' in licence.duration! ? generateMonthString(licence.duration.month!) : undefined,
    durationYear: 'year' in licence.duration! ? licence.duration.year!.toString() : undefined,
    hasAnotherLicence: Boolean(
      licence.countryOfFirstTest! !== 'GB' && isUKLicenceType(licence.type),
    ),
  }

  pageState.drivingLicenceNumber = licence.number
  pageState.skipDrivingLicenceNumberQuestion = licence.number ? false : true

  return pageState
}

type DriversState = NonNullable<PersistentState['pageState']['drivers']>

const getDriversState = (quote: QuoteV2): DriversState => {
  const { policyHolder, additionalDrivers } = quote

  const yearsInUk = resolveYearsInUk({
    dateOfBirth: policyHolder.dateOfBirth,
    bornInUk: policyHolder.bornInUk,
    yearsInUk: policyHolder.yearsInUk,
  })

  const pageState: DriversState = {
    title: policyHolder.name.title || undefined,
    firstName: policyHolder.name.firstName,
    lastName: policyHolder.name.lastName,
    email: policyHolder.email,
    emailSubscribe: true,
    dateOfBirth: convertIsoDateIntoDateObj(policyHolder.dateOfBirth),
    yearsInUk,
    address: policyHolder.currentAddress,
    homeowner: policyHolder.homeOwner,
    student: policyHolder.student !== null || policyHolder.employment.status === 'EDUCATION',
    additionalDrivers: {
      checked: additionalDrivers.length !== 0,
      list: additionalDrivers.map((driver) => {
        const isProvisionalLicence =
          driver.licence.provisional && driver.licence.type === LicenceType.UK
        const yearsInUk = resolveYearsInUk({
          dateOfBirth: driver.dateOfBirth,
          bornInUk: driver.bornInUk,
          yearsInUk: driver.yearsInUk,
        })

        const item = {
          id: driver.id,
          title: driver.name.title ?? '',
          firstName: driver.name.firstName,
          lastName: driver.name.lastName,
          drivingLicenceNumber: driver.licence.number,
          skipDrivingLicenceNumberQuestion: driver.licence.number ? false : true,
          dateOfBirth: convertIsoDateIntoDateObj(driver.dateOfBirth),
          yearsInUk,
          occupation: driver.employment.primary?.occupation.toUpperCase() ?? '',
          industry: driver.employment.primary?.industry.toUpperCase() ?? '',
          relationshipToPolicyHolder: driver.relationshipToPolicyHolder,
          licenceType: isProvisionalLicence
            ? LicenceType.UK_PROVISIONAL
            : (driver.licence.type ?? undefined),
          ukDurationMonth:
            driver.licence.ukDuration && 'month' in driver.licence.ukDuration
              ? generateMonthString(driver.licence.ukDuration.month!)
              : undefined,
          ukDurationYear:
            driver.licence.ukDuration && 'year' in driver.licence.ukDuration
              ? driver.licence.ukDuration.year!.toString()
              : undefined,
          durationMonth:
            'month' in driver.licence.duration!
              ? generateMonthString(driver.licence.duration.month!)
              : undefined,
          durationYear:
            'year' in driver.licence.duration!
              ? driver.licence.duration.year!.toString()
              : undefined,
          hasMedicalCondition:
            !driver.medicalCondition || driver.medicalCondition === MedicalCondition.NONE
              ? false
              : true,
          hasAnotherLicence:
            driver.licence.countryOfFirstTest === null
              ? undefined
              : driver.licence.countryOfFirstTest !== 'GB' && isUKLicenceType(driver.licence.type)
                ? true
                : false,
          employmentStatus:
            (driver.employment.status?.toUpperCase() as EmploymentStatus) ?? undefined,
          hasSecondaryEmployment: driver.employment.secondary?.occupation !== null,
          secondaryOccupation: driver.employment.secondary?.occupation?.toUpperCase() ?? undefined,
          secondaryIndustry: driver.employment.secondary?.industry?.toUpperCase() ?? undefined,
          nonMotoringConvictions: driver.nonMotoringConvictions ?? undefined,
        }

        return item satisfies DefaultValues<AdditionalDriverItem>
      }),
    },
    exitSurvey: "Don't know",
  }

  if (policyHolder.student !== null) {
    pageState.university = policyHolder.student.university
    pageState.course = policyHolder.student.course
    pageState.universityEmail = policyHolder.student.universityEmail || undefined
    pageState.drivenDuringTerm = policyHolder.student.drivenDuringTerm
  } else {
    pageState.employment = policyHolder.employment.primary?.occupation.toUpperCase() ?? ''
    pageState.industry = policyHolder.employment.primary?.industry.toUpperCase() ?? ''
    pageState.employmentStatus =
      (policyHolder.employment.status?.toUpperCase() as EmploymentStatus) ?? undefined
  }

  pageState.student =
    policyHolder.student !== null || policyHolder.employment.status === 'EDUCATION'
  pageState.hasSecondaryEmployment = policyHolder.employment.secondary?.occupation !== null
  pageState.secondaryEmployment = policyHolder.employment.secondary?.occupation?.toUpperCase()
  pageState.secondaryIndustry = policyHolder.employment.secondary?.industry?.toUpperCase()

  pageState.hasMedicalCondition =
    !policyHolder.medicalCondition || policyHolder.medicalCondition === MedicalCondition.NONE
      ? false
      : true
  pageState.medicalCondition = policyHolder.medicalCondition
    ? policyHolder.medicalCondition
    : undefined

  return pageState
}

type VehicleState = NonNullable<PersistentState['pageState']['vehicle']>
type VehicleFoundState = NonNullable<PersistentState['pageState']['vehicleFound']>

const getVehicleFoundState = (quote: QuoteV2): VehicleFoundState => {
  const { vehicle } = quote

  return {
    make: vehicle.make,
    model: vehicle.model,
    description: vehicle.description,
    price: vehicle.value,
    year: vehicle.year,
    numberOfSeats: vehicle.numberOfSeats,
    engineSize: vehicle.engineSize!,
    bodyType: vehicle.bodyType!,
    transmission: vehicle.transmission,
    fuel: vehicle.fuelType,
    firstRegistrationDate: vehicle.firstRegistrationDate,
    abiCode: vehicle.abiCode,
    groupRating: vehicle.groupRating,
    import: vehicle.import,
    tracker: vehicle.tracker!,
    vehicleType: vehicle.van ? VehicleType.VAN : VehicleType.CAR,
    grossWeight: vehicle.van?.grossWeight || null,
    regPlate: vehicle.regPlate || undefined,
    steering: vehicle.steering === 'LHD' ? VehicleSteering.LHD : VehicleSteering.RHD,
  }
}

const getVehicleState = (quote: QuoteV2): VehicleState => {
  const { vehicle, policyHolder } = quote

  const pageState: VehicleState = {
    purchased: Boolean(vehicle.purchaseDate),
    purchaseDate: vehicle.purchaseDate
      ? {
          year: String(vehicle.purchaseDate?.year),
          month: String(vehicle.purchaseDate?.month),
        }
      : {
          year: '',
          month: '',
        },
    modifications: {
      checked: 0 < vehicle.modificationAbiCodes.length,
      list: vehicle.modificationAbiCodes.map((modifiedPart) => ({
        modifiedPart,
      })),
    },
    overnightLocation: vehicle.overnightParking,
    mileage: vehicle.mileage.business + vehicle.mileage.personal,
    drivesOtherCars: policyHolder.drivesOtherCars,
    useOfOtherVehicle: (policyHolder.useOfOtherVehicle as OtherVehicleUse | null) ?? undefined,
    otherVehicleYears: policyHolder.ncd.otherVehicleYears ?? undefined,
    isRegisteredKeeper: vehicle.registeredKeeper === VehicleOwnerType.POLICY_HOLDER,
    registeredKeeper:
      vehicle.registeredKeeper === VehicleOwnerType.POLICY_HOLDER
        ? undefined
        : vehicle.registeredKeeper,
    isLegalOwner:
      vehicle.legalOwner === null
        ? undefined
        : vehicle.legalOwner === VehicleOwnerType.POLICY_HOLDER,
    legalOwner:
      vehicle.legalOwner === VehicleOwnerType.POLICY_HOLDER ? undefined : vehicle.legalOwner,
  }

  if (vehicle?.vehicleType !== VehicleType.VAN) {
    pageState.totalCarsInHousehold = policyHolder.totalCarsInHousehold ?? undefined
  }

  if (vehicle.van) {
    pageState.userInputtedGrossWeight = vehicle.van.grossWeight
    pageState.signage = vehicle.van.signage
    pageState.internalRacking = vehicle.van.internalRacking
    pageState.trailer = vehicle.van.trailer !== null
    pageState.refrigerationUnit = vehicle.van.refrigerationUnit
    pageState.tippingUnit = vehicle.van.tippingUnit
    pageState.visitsHazardousSites = vehicle.van.visitsHazardousSites
    pageState.carriesHazardousGoods = vehicle.van.carriesHazardousGoods
    pageState.badDriverHotline = vehicle.van.badDriverHotline
  }

  return pageState
}

const getClaims = (quote: QuoteV2) => {
  const { policyHolder, additionalDrivers } = quote

  const policyHolderClaims: ClaimItem[] = policyHolder.claims.map((claim) => ({
    driverId: POLICY_HOLDER_ID,
    month: claim.month,
    year: claim.year,
    type: claim.type,
    settlementType: claim.atFault ? SettlementType.AT_FAULT : SettlementType.NOT_AT_FAULT,
  }))

  const additionalDriversClaims = additionalDrivers.reduce<ClaimItem[]>((acc, driver) => {
    const driverClaims: ClaimItem[] = driver.claims.map((claim) => ({
      driverId: driver.id,
      month: claim.month,
      year: claim.year,
      type: claim.type,
      settlementType: claim.atFault ? SettlementType.AT_FAULT : SettlementType.NOT_AT_FAULT,
    }))

    acc.push(...driverClaims)

    return acc
  }, [])

  return [...policyHolderClaims, ...additionalDriversClaims]
}

const getConvictions = (quote: QuoteV2) => {
  const { policyHolder, additionalDrivers } = quote

  const policyHolderConvictions: ConvictionItem[] = policyHolder.convictions.map((conviction) => ({
    driverId: POLICY_HOLDER_ID,
    month: conviction.month,
    year: conviction.year,
    type: conviction.code,
  }))

  const additionalDriversConvictions: ConvictionItem[] = additionalDrivers.reduce<ConvictionItem[]>(
    (acc, driver) => {
      const driverConvictions = driver.convictions.map((claim) => ({
        driverId: driver.id,
        month: claim.month,
        year: claim.year,
        type: claim.code,
      }))

      acc.push(...driverConvictions)

      return acc
    },
    [],
  )

  return [...policyHolderConvictions, ...additionalDriversConvictions]
}

type HistoryState = NonNullable<PersistentState['pageState']['history']>

const getHistoryState = (quote: QuoteV2, showPreviousInsuranceCancelled: boolean): HistoryState => {
  const { policyHolder, anyDriverPreviousInsuranceCancelled } = quote

  const claims = getClaims(quote)
  const convictions = getConvictions(quote)

  const passedTheirTestInTheUK = policyHolder.licence.countryOfFirstTest === 'GB'

  const pageState: HistoryState = {
    claims: {
      checked: 0 < claims.length,
      list: claims,
    },
    convictions: {
      checked: 0 < convictions.length,
      list: convictions,
    },
    nonMotoringConvictions: policyHolder.nonMotoringConvictions ?? undefined,
  }

  if (policyHolder.licence.countryOfFirstTest !== null && !passedTheirTestInTheUK) {
    if (policyHolder.ncd.internationalYears !== null) {
      pageState.hasInternationalNcd = true
    } else {
      pageState.hasInternationalNcd = false
    }
  }

  if (!showPreviousInsuranceCancelled) {
    pageState.anyDriverPreviousInsuranceCancelled = Boolean(anyDriverPreviousInsuranceCancelled)
  }

  return pageState
}

type ClaimFreeDiscountState = NonNullable<PersistentState['pageState']['claimFreeDiscount']>

const getClaimFreeDiscountState = (quote: QuoteV2): ClaimFreeDiscountState => {
  const { policyHolder } = quote
  const defaultValues = getDefaultPageState(Page.CLAIM_FREE_DISCOUNT)

  const ncd: ClaimFreeDiscountState['ncd'] = {
    ukYears: policyHolder.ncd.ukYears,
  }

  const passedTheirTestInTheUK = policyHolder.licence.countryOfFirstTest === 'GB'

  if (!passedTheirTestInTheUK) {
    ncd.internationalYears =
      policyHolder.ncd.internationalYears ?? defaultValues.ncd?.internationalYears
  }

  const pageState: ClaimFreeDiscountState = {
    ncd,
  }

  return pageState
}

type PreviousInsurerCancelledState = NonNullable<
  PersistentState['pageState']['previousCancellation']
>
type PreviousInsurerCancelledDateState = NonNullable<
  PersistentState['pageState']['previousCancellationYear']
>
const getPreviousInsurerCancelledState = (
  quote: QuoteV2,
  showPreviousInsuranceCancelled: boolean,
): PreviousInsurerCancelledState => {
  if (showPreviousInsuranceCancelled) {
    return {
      previousInsuranceCancelled: Boolean(quote.policyHolder.previousInsuranceCancelled),
      wasCancelledByPolicyOwner: Boolean(
        quote.policyHolder.previousInsuranceCancelled &&
          quote.policyHolder.previousInsuranceCancelledInfo?.reason !==
            PreviousInsuranceCancellationReasons.ADDITIONAL_DRIVER_CANCELLED,
      ),
      previousInsuranceCancelledReason: quote.policyHolder.previousInsuranceCancelledInfo?.reason,
    }
  }

  return {}
}

const getPreviousInsurerCancelledYearState = (
  quote: QuoteV2,
  showPreviousInsuranceCancelled: boolean,
): PreviousInsurerCancelledDateState => {
  if (showPreviousInsuranceCancelled) {
    return {
      previousInsuranceCancelledYear:
        quote.policyHolder.previousInsuranceCancelledInfo?.cancellationYear,
    }
  }

  return {}
}

const getRegistrationState = (
  quote: QuoteV2,
): NonNullable<PersistentState['pageState']['registration']> => {
  return { regPlate: quote.vehicle.regPlate || undefined }
}

const getPolicyStartDateState = (
  quote: QuoteV2,
  growth_payment_intention_question?: TestGroup,
): NonNullable<PersistentState['pageState']['quote']> => {
  return {
    inceptionDate: quote.inceptionDate,
    paymentTypeIntention:
      growth_payment_intention_question === 'VARIANT' && quote.paymentTypeIntention !== null
        ? quote.paymentTypeIntention
        : undefined,
  }
}

interface TransformQuoteV2ToPageStateArgs {
  data: QuoteV2
  showPreviousInsuranceCancelled: boolean
  abTests?: PossibleAbTests
  featureFlags?: PossibleFeatureFlags
}
export const transformQuoteV2ToPageState = ({
  data,
  showPreviousInsuranceCancelled,
  abTests,
}: TransformQuoteV2ToPageStateArgs): Required<PersistentState['pageState']> => {
  const pageState = {
    [Page.REGISTRATION]: getRegistrationState(data),
    [Page.MVL]: getDefaultPageState(Page.MVL),
    [Page.VEHICLE_FOUND]: getVehicleFoundState(data),
    [Page.LICENCE]: getLicenceState(data),
    [Page.DRIVERS]: getDriversState(data),
    [Page.VEHICLE]: getVehicleState(data),
    [Page.HISTORY]: getHistoryState(data, showPreviousInsuranceCancelled),
    [Page.CLAIM_FREE_DISCOUNT]: getClaimFreeDiscountState(data),
    [Page.PREVIOUS_CANCELLATION]: getPreviousInsurerCancelledState(
      data,
      showPreviousInsuranceCancelled,
    ),
    [Page.PREVIOUS_CANCELLATION_YEAR]: getPreviousInsurerCancelledYearState(
      data,
      showPreviousInsuranceCancelled,
    ),
    [Page.POLICY_START_DATE]: getPolicyStartDateState(
      data,
      abTests?.growth_payment_intention_question,
    ),
    [Page.USAGE]: { usageType: data.vehicle.usage },
  }

  return pageState
}
